import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon, BdsChipClickable } from '@/core/constants/webComponents';
import { useDebouncedSearch } from '@/core/hooks/useDebouncedSearch';
import { useClickOutside } from '@/core/hooks/useClickOutside';
import { useDropdownAutoScroll } from '@/core/hooks/useDropdownAutoScroll';
import { AuthorOption } from '@/core/services/interfaces/AuthorInterfaces';
import { type SelectionMap, type SelectionState } from '@/core/components/FilterSelector/types';
import { CustomTooltip } from '@/core/components/CustomTooltip/CustomTooltip';
import { ChannelIcon } from '../../ChannelIcon';
import { getAuthorKey } from '../../AuthorsContent.defaults';
import './AuthorSearchSelect.scss';

const MINIMUM_SEARCH_LENGTH = 3;

export interface AuthorSearchSelectProps {
  label: string;
  placeholder: string;
  helperText: string;
  loadingText: string;
  emptyText: string;
  searchAuthors: (term: string) => Promise<AuthorOption[]>;
  selection: SelectionMap;
  authorsMap: Record<string, AuthorOption>;
  onSelectionChange: (updater: React.SetStateAction<SelectionMap>, authorToAdd?: AuthorOption) => void;
  allowNegation?: boolean;
}

const getNextSelectionState = (current: SelectionState | null, allowNegation: boolean): SelectionState | null => {
  if (current === null) return 'add';
  if (current === 'add') return allowNegation ? 'remove' : null;
  return null;
};

export const AuthorSearchSelect: React.FC<AuthorSearchSelectProps> = ({
  label,
  placeholder,
  helperText,
  loadingText,
  emptyText,
  searchAuthors,
  selection,
  authorsMap,
  onSelectionChange,
  allowNegation = false,
}) => {
  const { t } = useTranslation();
  const {
    searchTerm,
    isOpen,
    setIsOpen,
    isLoading,
    searchResults,
    handleSearchTermChange,
    closeDropdown,
  } = useDebouncedSearch<AuthorOption>({ searchFn: searchAuthors, minLength: MINIMUM_SEARCH_LENGTH });

  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLLabelElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef<(HTMLElement | null)[]>([]);

  const outsideRefs = useMemo(() => [fieldRef, dropdownRef], []);
  
  useClickOutside(outsideRefs, closeDropdown, isOpen);

  const handleToggleAuthor = useCallback(
    (option: AuthorOption) => {
      const key = getAuthorKey(option);
      const currentState = selection[key] ?? null;
      const nextState = getNextSelectionState(currentState, allowNegation);
      
      onSelectionChange((prev) => {
        const updated = { ...prev };
        if (nextState === null) {
          delete updated[key];
        } else {
          updated[key] = nextState;
        }
        return updated;
      }, option);
      
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [selection, allowNegation, onSelectionChange],
  );

  const handleRemoveAuthor = useCallback((key: string) => {
    onSelectionChange((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, [onSelectionChange]);

  useEffect(() => {
    const cleanups: (() => void)[] = [];
    Object.entries(selection).forEach(([key, state], index) => {
      if (state === null) return;
      const chipElement = chipRefs.current[index];
      if (!chipElement) return;
      const closeHandler = (event: Event) => {
        event.stopPropagation();
        handleRemoveAuthor(key);
      };
      chipElement.addEventListener('chipClickableClose', closeHandler);
      cleanups.push(() => chipElement.removeEventListener('chipClickableClose', closeHandler));
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [selection, handleRemoveAuthor]);

  const isSearchActive = searchTerm.trim().length >= MINIMUM_SEARCH_LENGTH;
  const showDropdown = isOpen && isSearchActive;
  const hasResults = searchResults.length > 0;

  const selectedEntries = useMemo(() => 
    Object.entries(selection).filter(([, state]) => state !== null),
    [selection]
  );

  useDropdownAutoScroll(showDropdown, dropdownRef, selectedEntries.length);

  return (
    <div className="author-select" ref={containerRef}>
      <span id="author-search-label" className="author-select__label">
        {label}
      </span>

      <div className="author-select__wrapper">
        <label
          ref={fieldRef}
          className={`author-select__field${showDropdown ? ' author-select__field--open' : ''}`}
          htmlFor="author-search-input"
        >
          {selectedEntries.map(([authorKey, state], index) => {
            const author = authorsMap[authorKey];
            if (!author) return null;
            
            const chipLabel = `${author.name} (${author.channel})`;
            const isTruncated = chipLabel.length > 40;
            
            const chipButton = (
              <button
                type="button"
                className={`author-select__chip-wrapper ${state === 'remove' ? 'author-select__chip-wrapper--negated' : ''}`}
                onClick={(event) => event.stopPropagation()}
              >
                <BdsChipClickable
                  ref={(element: HTMLElement | null) => {
                    chipRefs.current[index] = element;
                  }}
                  color="outline"
                  close
                >
                  {state === 'remove' && (
                    <span className="author-select__negation-icon">
                      <BdsIcon name="delete" size="x-small" theme="outline" />
                    </span>
                  )}
                  <span className="author-select__chip-content">
                    {chipLabel}
                  </span>
                </BdsChipClickable>
              </button>
            );

            if (isTruncated) {
              return (
                <CustomTooltip key={authorKey} content={chipLabel} position="top">
                  {chipButton}
                </CustomTooltip>
              );
            }

            return <React.Fragment key={authorKey}>{chipButton}</React.Fragment>;
          })}

          <input
            id="author-search-input"
            ref={inputRef}
            className="author-select__input"
            value={searchTerm}
            placeholder={selectedEntries.length === 0 ? placeholder : ''}
            onChange={handleSearchTermChange}
            onFocus={() => setIsOpen(true)}
            autoComplete="off"
            aria-labelledby="author-search-label"
          />

          <span
            className={`author-select__arrow${showDropdown ? ' author-select__arrow--open' : ''}`}
            aria-hidden="true"
          >
            <BdsIcon name="arrow-down" size="x-small" />
          </span>
        </label>

        {showDropdown && (
          <div className="author-select__dropdown" ref={dropdownRef}>
            <ul className="author-select__list">
              {isLoading && (
                <li className="author-select__option author-select__option--message">
                  <span className="author-select__message-text">{loadingText}</span>
                </li>
              )}

              {!isLoading && !hasResults && (
                <li className="author-select__option author-select__option--message">
                  <span className="author-select__message-text">{emptyText}</span>
                </li>
              )}

              {!isLoading &&
                hasResults &&
                searchResults.map((option) => {
                  const key = getAuthorKey(option);
                  const state = selection[key] ?? null;
                  const checkboxClass = `author-select__checkbox author-select__checkbox--${state ?? 'empty'}`;
                  
                  const getAriaLabel = () => {
                    if (state === 'add') return t('globalFilter.authorIncluded', '{{name}} - included', { name: option.name });
                    if (state === 'remove') return t('globalFilter.authorExcluded', '{{name}} - excluded', { name: option.name });
                    return t('globalFilter.authorUnselected', '{{name}} - unselected', { name: option.name });
                  };
                  
                  return (
                    <li className="author-select__option" key={key}>
                      <button
                        type="button"
                        className="author-select__option-btn"
                        aria-label={getAriaLabel()}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleToggleAuthor(option)}
                      >
                        <span className={checkboxClass}>
                          {state === 'add' && <span className="author-select__check-icon">✓</span>}
                          {state === 'remove' && <span className="author-select__check-icon">−</span>}
                        </span>

                        <ChannelIcon channel={option.channel} />
                        <span className="author-select__option-texts">
                          <span className="author-select__option-name">{option.name}</span>
                          <span className="author-select__option-username">
                            {option.username}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </div>

      <span className="author-select__helper">
        <BdsIcon name="info" size="x-small" />
        {helperText}
      </span>
    </div>
  );
};
